import React from 'react';
import BeaconForm from "pages/Beacon/Form";
import Page from "components/Page";
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {FormikHelpers} from "formik";
import {invalidate} from "services/query-client";
import {BeaconData} from "types/Beacon";

function MutateBeacon() {
    const {id} = useParams()
    const {data: beacon, isLoading: beaconsLoading} = useQuery(
        ['beacons', {id}],
        () => ngage.beacons.get(parseInt(id as string)),
        {enabled: !!id}
    )
    const {data: nextConfiguration, isLoading: nextConfigLoading} = useQuery(
        ['beacons.next'],
        () => ngage.beacons.getNextConfiguration(),
        {enabled: !id}
    )

    const store = useMutation((data: BeaconData) => ngage.beacons.store(data))
    const update = useMutation((data: BeaconData) => ngage.beacons.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: BeaconData, {setSubmitting}: FormikHelpers<BeaconData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('beacons.all')
        await invalidate('beacons.next')

        setSubmitting(false)
        navigate('/beacons')
    }

    // Handle store mutation
    async function handleStore(values: BeaconData) {
        try {
            await store.mutateAsync(values)
            notify("success", 'Beacon Created', `${values.name} has been created`)
        } catch (error) {
            notify("error", 'Beacon Not Created', `${values.name} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: BeaconData) {
        try {
            await update.mutateAsync(values)
            notify("success", 'Beacon Updated', `${values.name} has been updated`)
            await invalidate(['beacons', {id}])
        } catch (error) {
            notify("error", 'Beacon Not Updated', `${values.name} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Operations': '/', 'Beacons': '/beacons', 'New Beacon': '/beacons/new'}
    const editBreadcrumbs = {'Operations': '/', 'Beacons': '/beacons', 'Edit Beacon': `/beacons/${id}/edit`}

    return (
        <Page title={`${id? 'Edit': 'New'} Beacon`}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={beaconsLoading || nextConfigLoading}>
            <BeaconForm beacon={beacon} nextConfig={nextConfiguration} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateBeacon;